import { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  toast,
} from 'prosci-ui';
import { Info } from '@/lib/icons';
import {
  ProximaApiKeysTab,
  type ApiKeysVariantConfig,
  type CreateDialogProps,
} from './proxima-api-keys-tab';

function PortfolioCreateDialog({ open, onClose, onCreate, isCreating }: CreateDialogProps) {
  const [keyName, setKeyName] = useState('');

  useEffect(() => {
    if (!open) setKeyName('');
  }, [open]);

  const handleCreate = () => {
    if (!keyName.trim()) {
      toast.error('Please enter a name for the portfolio key');
      return;
    }
    onCreate({ name: keyName.trim() }, keyName.trim());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a Portfolio Key</DialogTitle>
          <DialogDescription>
            API keys allow your organization to securely integrate with external services. Add a key
            to get started with seamless, centralized API access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Alert variant="neutral">
            <Info />
            <AlertTitle>Access scope</AlertTitle>
            <AlertDescription>
              This API key has automatic access to all projects where your organization users are
              owners of.
            </AlertDescription>
          </Alert>
          <Field>
            <FieldLabel>Name<span className="text-destructive">*</span></FieldLabel>
            <Input
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              className="mt-2"
              placeholder="e.g. My Portfolio Key"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating} className="bg-teal-900">
            {isCreating ? 'Generating…' : 'Generate API Key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PORTFOLIO_CONFIG: ApiKeysVariantConfig = {
  keyType: 'organization',
  emptyTitle: 'No Portfolio API Keys Added Yet',
  emptyDescription:
    'API keys allow your organization to securely integrate with external services. Add a key to get started with seamless, centralized API access.',
  addButtonLabel: 'Add Portfolio Key',
  sectionTitle: 'Proxima Portfolio API Keys',
  sectionDescription:
    'Create a new portfolio API key for a specific integration service. Once created, the key will be masked for security.',
  tableHeader: 'Portfolio key name',
  toastCreated: 'API key created successfully',
  toastCreateError: 'Failed to create API key',
  toastRevoked: 'API key revoked',
  toastRevokeError: 'Failed to revoke API key',
  toastBulkRevoked: 'Selected API keys revoked',
  toastBulkRevokeError: 'Failed to revoke one or more keys',
  CreateDialog: PortfolioCreateDialog,
};

export function ProximaPortfolioApiKeysTab() {
  return <ProximaApiKeysTab config={PORTFOLIO_CONFIG} />;
}
